import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import { UploadCloud, FileSpreadsheet, Link2, Loader2, CheckCircle2 } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Progress } from '../ui/progress';

export const ImportContactsDialog = ({ open, onOpenChange, onImported }) => {
  const fileRef = useRef(null);
  const [step, setStep] = useState(1);
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState([]);
  const [sheetLink, setSheetLink] = useState('');
  const [tag, setTag] = useState('');
  const [importing, setImporting] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const reset = () => { setStep(1); setPreview([]); setSheetLink(''); setTag(''); setParsing(false); setImporting(false); };

  const handleClose = (v) => { if (!v) reset(); onOpenChange(v); };

  const parseFile = async (file) => {
    if (!file) return;
    setParsing(true);
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await api.post('/contacts/parse-file', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.count === 0) { toast.error('No valid contacts with emails found in that file.'); setParsing(false); return; }
      setPreview(res.data.contacts);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not read that file');
    } finally { setParsing(false); }
  };

  const parseSheet = async () => {
    if (!sheetLink.trim()) { toast.error('Paste a Google Sheets link first'); return; }
    setParsing(true);
    try {
      const res = await api.post('/contacts/parse-gsheet', { link: sheetLink });
      if (res.data.count === 0) { toast.error('No valid contacts found in that sheet.'); setParsing(false); return; }
      setPreview(res.data.contacts);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not import that sheet');
    } finally { setParsing(false); }
  };

  const confirmImport = async () => {
    setImporting(true);
    try {
      const tags = tag.trim() ? [tag.trim()] : [];
      const res = await api.post('/contacts/import', { contacts: preview, tags });
      toast.success(`Imported ${res.data.inserted} contacts${res.data.skipped ? `, skipped ${res.data.skipped} duplicates` : ''}`);
      onImported && onImported();
      handleClose(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl" data-testid="contacts-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Import contacts</DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <Tabs defaultValue="file" className="mt-2">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="file" data-testid="import-tab-file">Upload file</TabsTrigger>
              <TabsTrigger value="sheet" data-testid="import-tab-sheet">Google Sheets</TabsTrigger>
            </TabsList>

            <TabsContent value="file" className="mt-4">
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); parseFile(e.dataTransfer.files[0]); }}
                data-testid="contacts-file-dropzone"
                className={`cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
              >
                {parsing ? (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-8 w-8 text-primary animate-spin" />
                    <p className="text-sm text-muted-foreground">Reading your file…</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                      <UploadCloud className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">Drop your file here or click to browse</p>
                      <p className="text-sm text-muted-foreground mt-1">Excel (.xlsx) or CSV with an email column</p>
                      <p className="text-xs text-muted-foreground mt-1">We auto-detect name, email, phone, company, address &amp; dietary/allergies columns</p>
                    </div>
                  </div>
                )}
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
                  onChange={(e) => parseFile(e.target.files[0])} data-testid="contacts-file-input" />
              </div>
            </TabsContent>

            <TabsContent value="sheet" className="mt-4 space-y-4">
              <div className="rounded-xl bg-secondary/60 p-4 flex gap-3">
                <FileSpreadsheet className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground">Share your Google Sheet as <strong>“Anyone with the link can view”</strong>, then paste the link below. We&apos;ll auto-detect name, email, phone, company, address and dietary/allergies columns.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Google Sheets link</Label>
                <Input value={sheetLink} onChange={(e) => setSheetLink(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/..."
                  data-testid="import-gsheet-input" className="h-11" />
              </div>
              <Button onClick={parseSheet} disabled={parsing} className="w-full h-11 gap-2" data-testid="import-gsheet-button">
                {parsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                {parsing ? 'Importing…' : 'Preview contacts'}
              </Button>
            </TabsContent>
          </Tabs>
        )}

        {step === 2 && (
          <div className="mt-2">
            <div className="flex items-center gap-2 text-primary mb-3">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-medium">{preview.length} valid contacts found</span>
            </div>
            <div className="rounded-xl border border-border max-h-64 overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-secondary">
                  <tr><th className="text-left px-3 py-2 font-medium">Name</th><th className="text-left px-3 py-2 font-medium">Email</th><th className="text-left px-3 py-2 font-medium">Phone</th><th className="text-left px-3 py-2 font-medium">Company</th></tr>
                </thead>
                <tbody>
                  {preview.slice(0, 100).map((c, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-3 py-2">{`${c.first_name} ${c.last_name}`.trim() || '—'}</td>
                      <td className="px-3 py-2 text-muted-foreground">{c.email}</td>
                      <td className="px-3 py-2 text-muted-foreground">{c.phone || '—'}</td>
                      <td className="px-3 py-2 text-muted-foreground">{c.company || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 space-y-1.5">
              <Label>Add a tag to these contacts (optional)</Label>
              <Input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="e.g. Family, Work, VIP" className="h-11" data-testid="import-tag-input" />
            </div>
            <div className="flex gap-3 mt-5">
              <Button variant="secondary" onClick={() => setStep(1)} className="flex-1 h-11">Back</Button>
              <Button onClick={confirmImport} disabled={importing} className="flex-1 h-11 gap-2" data-testid="contacts-import-confirm-button">
                {importing && <Loader2 className="h-4 w-4 animate-spin" />}
                {importing ? 'Importing…' : `Import ${preview.length} contacts`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
